//@ts-nocheck
import React from "react";
import { motion } from "framer-motion";
import { resolveAstriaKoreaV2Tab } from "./AstriaKoreaV2Card";

// ─────────────────────────────────────────────────────────────────────────────
// ASTRIA KOREA V2 — Guided suggestions (compact chip row).
// Shown just above the input box the first time a user enters a Korea V2
// chat tab (before any messages exist), so they see a few tappable starter
// questions instead of a bare textbox. Deliberately lightweight — a single
// row of pill chips, not a bordered card — so it doesn't compete with the
// chat transcript above or crowd the input below.
//
// Scope: Astria Korea V2 chat tabs (Daily Flow / Life Map / Relationship /
// Daily Companion) ONLY — the Compatibility tab uses its own form flow.
// ─────────────────────────────────────────────────────────────────────────────

const KR_FONT =
  "'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";

const COLORS = {
  textMuted: "#8B8594",
  chipBg: "#FFFFFF",
  chipBorder: "#E9E3F5",
  chipText: "#4A4550",
  chipHoverBg: "#F4EFFB",
  chipHoverBorder: "#B9A6DE",
};

// Korean prompts
const STARTER_PROMPTS_KR: Record<string, string[]> = {
  daily_flow: [
    "오늘 저에게 어울리는 하루 흐름이 궁금해요",
    "오늘 기분이 왜 이런지 알고 싶어요",
    "오늘 날씨에 맞는 하루를 보내고 싶어요",
  ],
  life_map: [
    "오늘 저와 잘 어울리는 동네가 어디일까요?",
    "오늘 기분에 맞는 음식이 궁금해요",
    "혼자 있기 좋은 카페를 추천해주세요",
  ],
  relationship: [
    "우리 두 사람 궁합이 어때요?",
    "우리가 자주 부딪히는 이유가 궁금해요",
    "우리 관계, 지금 어떤 타이밍일까요?",
  ],
  daily_companion: [
    "오늘 하루를 어떻게 보내면 좋을까요?",
    "요즘 마음이 조금 지쳐요.",
    "오늘 저녁은 무엇을 해야 마음이 편해질까요?",
  ],
};

// English prompts
const STARTER_PROMPTS_EN: Record<string, string[]> = {
  daily_flow: [
    "I'm curious about the flow of my day today",
    "I want to know why I feel this way today",
    "I want to spend today according to the weather",
  ],
  life_map: [
    "Which neighborhood suits me well today?",
    "I'm curious about food that matches my mood today",
    "Recommend a good cafe to be alone",
  ],
  relationship: [
    "How compatible are we?",
    "I'm curious why we often clash",
    "What timing is our relationship at right now?",
  ],
  daily_companion: [
    "How should I spend my day today?",
    "I've been feeling a bit exhausted lately",
    "What can I do this evening to feel at ease?",
  ],
};

// Korean labels
const TAB_LABEL_KR: Record<string, string> = {
  daily_flow: "이렇게 물어보세요",
  life_map: "이렇게 물어보세요",
  relationship: "이렇게 물어보세요",
  daily_companion: "이렇게 물어보세요",
};

// English labels
const TAB_LABEL_EN: Record<string, string> = {
  daily_flow: "Try asking these",
  life_map: "Try asking these",
  relationship: "Try asking these",
  daily_companion: "Try asking these",
};

interface AstriaKoreaV2SuggestionsProps {
  subCategoryName?: string | null;
  onSelectPrompt: (text: string) => void;
}

const AstriaKoreaV2Suggestions: React.FC<AstriaKoreaV2SuggestionsProps> = ({
  subCategoryName,
  onSelectPrompt,
}) => {
  // Astria Korea V2/V3 is a Korean-only lane — the backend always replies in
  // Korean regardless of the detected/preferred language (see "LANGUAGE
  // RULE" in AstriaKoreaV2Service.js / AstriaKoreaV3Service.js prompts), so
  // the starter chips must always be Korean too. Previously this fell back
  // to English whenever preferredLanguage/region wasn't explicitly Korean,
  // which is exactly the "English leakage in the KR lane" the client
  // flagged. The English prompt set is kept defined (unused here) in case
  // another, non-Korea-locked caller wants it later.
  const shouldShowKorean = true;

  const tabKey = resolveAstriaKoreaV2Tab(subCategoryName) || "daily_companion";

  // Select prompts and labels based on language
  const prompts = shouldShowKorean
    ? STARTER_PROMPTS_KR[tabKey]
    : STARTER_PROMPTS_EN[tabKey];

  const label = shouldShowKorean ? TAB_LABEL_KR[tabKey] : TAB_LABEL_EN[tabKey];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      style={{ fontFamily: shouldShowKorean ? KR_FONT : "inherit" }}
    >
      <div
        style={{
          fontSize: 12.5,
          color: COLORS.textMuted,
          marginBottom: 10,
          paddingLeft: 2,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
        {prompts.map((text) => (
          <button
            key={text}
            type="button"
            onClick={() => onSelectPrompt(text)}
            style={{
              padding: "9px 16px",
              borderRadius: 999,
              background: COLORS.chipBg,
              border: `1px solid ${COLORS.chipBorder}`,
              boxShadow: "0 2px 10px rgba(109,76,146,0.05)",
              color: COLORS.chipText,
              fontSize: 13,
              fontFamily: shouldShowKorean ? KR_FONT : "inherit",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "background 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = COLORS.chipHoverBg;
              el.style.borderColor = COLORS.chipHoverBorder;
              el.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = COLORS.chipBg;
              el.style.borderColor = COLORS.chipBorder;
              el.style.transform = "translateY(0)";
            }}
          >
            {text}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default AstriaKoreaV2Suggestions;
